import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check } from 'lucide-react';
import { Button } from '../ui';
import { MusicIcon, VisualArtIcon, WritingIcon, VideoIcon, SoftwareIcon, Layers } from '../icons';
import { File } from 'lucide-react';
import { ProjectDto, CreateProjectRequest, WorkflowTemplateDto, workflowTemplatesApi, isSystemTemplate, ProjectCategory } from '../../services';
import './ProjectForm.css';

interface ProjectFormProps {
  project?: ProjectDto | null;
  initialStage?: string;
  onSubmit: (data: CreateProjectRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const WORK_TYPES = [
  { value: 'Music', labelKey: 'works.types.music', icon: <MusicIcon size={20} /> },
  { value: 'Art', labelKey: 'works.types.art', icon: <VisualArtIcon size={20} /> },
  { value: 'Writing', labelKey: 'works.types.writing', icon: <WritingIcon size={20} /> },
  { value: 'Video', labelKey: 'works.types.video', icon: <VideoIcon size={20} /> },
  { value: 'Software', labelKey: 'works.types.software', icon: <SoftwareIcon size={20} /> },
];

const WORK_CATEGORIES: { value: ProjectCategory; labelKey: string; descriptionKey: string; icon: React.ReactNode }[] = [
  { value: 'Opus', labelKey: 'works.categories.opus', descriptionKey: 'works.categories.opusDescription', icon: <Layers size={20} /> },
  { value: 'Piece', labelKey: 'works.categories.piece', descriptionKey: 'works.categories.pieceDescription', icon: <File size={20} /> },
];

export const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  initialStage,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(project?.title || '');
  const [description, setDescription] = useState(project?.description || '');
  const [projectType, setProjectType] = useState(project?.projectType || 'Music');
  const [category, setCategory] = useState<ProjectCategory>(project?.category || 'Piece');
  const [selectedStage, setSelectedStage] = useState(initialStage || project?.currentStage || '');
  const [targetDate, setTargetDate] = useState(project?.targetCompletionDate?.split('T')[0] || '');
  const [template, setTemplate] = useState<WorkflowTemplateDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!project;

  // Fetch the system template for the selected project type and category
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        console.log('[ProjectForm] Fetching templates for type:', projectType, 'category:', category);
        const templates = await workflowTemplatesApi.getAll(projectType, category);
        console.log('[ProjectForm] Got templates:', templates);
        // Get the system template for this project type and category
        const systemTemplate = templates.find((t) => isSystemTemplate(t));
        console.log('[ProjectForm] System template:', systemTemplate);
        if (systemTemplate) {
          setTemplate(systemTemplate);
          // Auto-select first stage
          if (systemTemplate.stages.length > 0) {
            setSelectedStage(systemTemplate.stages[0]);
          }
        } else {
          console.warn('[ProjectForm] No system template found for type:', projectType, 'category:', category);
          setTemplate(null);
        }
      } catch (err) {
        console.error('[ProjectForm] Failed to fetch template:', err);
        setTemplate(null);
      }
    };

    fetchTemplate();
  }, [projectType, category]);

  // Reset stage when project type or category changes
  const handleProjectTypeChange = (type: string) => {
    setProjectType(type);
    setSelectedStage(''); // Will be set by useEffect when template loads
  };

  const handleCategoryChange = (cat: ProjectCategory) => {
    setCategory(cat);
    setSelectedStage(''); // Will be set by useEffect when template loads
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError(t('works.form.title') + ' is required');
      return;
    }

    if (!selectedStage) {
      setError(t('works.form.selectStage'));
      return;
    }

    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        projectType,
        category,
        initialStage: selectedStage,
        workflowTemplateId: template?.id,
        targetCompletionDate: targetDate || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('works.errors.createFailed'));
    }
  };

  return (
    <div className="project-form-overlay">
      <div className="project-form">
        <div className="project-form__header">
          <h2 className="project-form__title">
            {isEditing ? t('works.editWork') : t('works.createWork')}
          </h2>
          <button className="project-form__close" onClick={onCancel} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="project-form__error">{error}</div>}

          <div className="project-form__field">
            <label htmlFor="title">{t('works.form.title')} *</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('works.form.titlePlaceholder')}
              maxLength={100}
              required
            />
          </div>

          <div className="project-form__field">
            <label htmlFor="description">{t('works.form.description')}</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('works.form.descriptionPlaceholder')}
              rows={3}
            />
          </div>

          <div className="project-form__field">
            <label>{t('works.form.projectType')}</label>
            <div className="project-form__type-grid">
              {WORK_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={`project-form__type-btn ${projectType === type.value ? 'project-form__type-btn--selected' : ''}`}
                  onClick={() => handleProjectTypeChange(type.value)}
                >
                  <span className="project-form__type-icon">{type.icon}</span>
                  <span className="project-form__type-label">{t(type.labelKey)}</span>
                  {projectType === type.value && <Check size={16} className="project-form__type-check" />}
                </button>
              ))}
            </div>
          </div>

          <div className="project-form__field">
            <label>{t('works.form.category')}</label>
            <div className="project-form__category-grid">
              {WORK_CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  className={`project-form__category-btn ${category === cat.value ? 'project-form__category-btn--selected' : ''}`}
                  onClick={() => handleCategoryChange(cat.value)}
                >
                  <span className="project-form__category-icon">{cat.icon}</span>
                  <span className="project-form__category-content">
                    <span className="project-form__category-label">{t(cat.labelKey)}</span>
                    <span className="project-form__category-description">{t(cat.descriptionKey)}</span>
                  </span>
                  {category === cat.value && <Check size={16} className="project-form__category-check" />}
                </button>
              ))}
            </div>
          </div>

          {template && (
            <div className="project-form__field">
              <label>{t('works.form.initialStage')}</label>
              <div className="project-form__stage-grid">
                {template.stages.map((stage, index) => (
                  <button
                    key={stage}
                    type="button"
                    className={`project-form__stage-btn ${selectedStage === stage ? 'project-form__stage-btn--selected' : ''}`}
                    onClick={() => setSelectedStage(stage)}
                  >
                    <span className="project-form__stage-number">{index + 1}</span>
                    <span className="project-form__stage-name">{stage}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="project-form__field">
            <label htmlFor="targetDate">{t('works.form.targetDate')}</label>
            <input
              id="targetDate"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="project-form__actions">
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? t('common.loading') : isEditing ? t('common.save') : t('common.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;
